import { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { ARTIFACT_ID } from 'domain/shared/artifact';
import { ArtifactUploaderInput } from 'domain/shared/artifact/input';
import { DownloadFileLinkField } from 'domain/shared/artifact/field';
import { useDocumentStorageContext } from 'framework/documentStorage';
import { DocumentStorageItemType } from 'framework/documentStorage';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import { useShowContext } from 'framework/show';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { DEFAULT_ERROR_MESSAGE } from 'framework/form';
import { useAuthorization } from 'framework/authorization';
import { SmallLoader } from 'framework/ui/mui-builder';
import {
  UPDATE_EMPLOYEE_DOCUMENTS,
  DOWNLOAD_EMPLOYEE_DOCUMENTS,
  EMPLOYEE_DOCUMENT_STAGES_ALIAS,
  EMPLOYEE_DOCUMENT_SECTIONS,
  EMPLOYEE_DOCUMENT_HR_TYPES,
} from '../../constants';
import { isPolishOffice } from 'domain/shared/employee';

const ScanSection = () => {
  const currentAlias = useEmployeeId()!;
  const { isGranted } = useAuthorization();
  const { getDocumentByPaths, uploadDocument, editDocument } = useDocumentStorageContext();
  const { data, setData } = useShowContext();
  const { addMessage } = useMessageService();

  const [isEditMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [scanFileData, setScanFileData] = useState<Nullable<DocumentStorageItemType>>(null);
  const [tempArtifactIds, setTempArtifactIds] = useState<string[]>([]);

  const oldData = data.getData();
  const isPolishContractEmployee = data.getValueBySource('isPolishContractEmployee');
  const { templateId, office, category, section, fileType } = data.getValueBySource('metadata') || {};
  const canEdit = isGranted(UPDATE_EMPLOYEE_DOCUMENTS);

  const isShowCategory = isPolishContractEmployee && isPolishOffice(String(office));

  const isCategoryRequiredToAddScan = isShowCategory && templateId && !category;
  const editScanTooltip = isCategoryRequiredToAddScan ? 'Category is required to add scan for this document.' : '';

  const exitEditMode = useCallback(() => {
    setEditMode(false);
    setTempArtifactIds([]);
  }, []);

  const toggleEditMode = useCallback(() => {
    setEditMode(prev => !prev);
  }, []);

  const handleArtifactsUpload = useCallback((values: any) => {
    setTempArtifactIds(values([]).paths);
  }, []);

  const updateScanMetadata = useCallback(
    async (scans: string[] | null) => {
      const newMetadata = {
        ...oldData.metadata,
        stage:
          scans &&
          templateId &&
          section === EMPLOYEE_DOCUMENT_SECTIONS.HR &&
          fileType !== EMPLOYEE_DOCUMENT_HR_TYPES.OTHER
            ? EMPLOYEE_DOCUMENT_STAGES_ALIAS.SIGNED
            : oldData.metadata.stage,
        scans: scans || undefined,
        paths: [oldData.path],
      };

      await editDocument(newMetadata);

      setData?.({ ...oldData, metadata: { ...oldData.metadata, stage: newMetadata.stage, scans: newMetadata.scans } });

      if (!scans) setScanFileData(null);
    },
    [editDocument, fileType, oldData, section, setData, templateId],
  );

  const handleRemoveScanDocument = useCallback(async () => {
    setIsDeleting(true);
    try {
      await updateScanMetadata(null);
    } catch {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    } finally {
      setIsDeleting(false);
    }
  }, [updateScanMetadata, addMessage]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      if (!tempArtifactIds.length) {
        await updateScanMetadata(null);
      } else {
        const uploaded = await uploadDocument({
          employeeAlias: currentAlias,
          destination: 'scans',
          paths: tempArtifactIds,
        });
        await updateScanMetadata(uploaded.paths);
      }
      exitEditMode();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error saving scans:', e);
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    } finally {
      setIsSaving(false);
    }
  }, [tempArtifactIds, currentAlias, uploadDocument, updateScanMetadata, addMessage, exitEditMode]);

  const fetchScanData = useCallback(
    async (paths: string[]) => {
      try {
        const result = await getDocumentByPaths(paths);
        setScanFileData(result[0]);
      } catch {
        addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
      }
    },
    [getDocumentByPaths, addMessage],
  );

  useEffect(() => {
    if (oldData?.metadata?.scans?.length) {
      fetchScanData(oldData.metadata.scans);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oldData?.metadata?.scans]);

  const isLoading = useMemo(() => isSaving || isDeleting, [isSaving, isDeleting]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" color="primary">
          Scan:
        </Typography>

        {canEdit && (
          <Box display="flex" alignItems="center">
            {!isEditMode ? (
              <Tooltip title={editScanTooltip}>
                <span>
                  <IconButton
                    onClick={toggleEditMode}
                    data-testid="edit-button"
                    disabled={isLoading || isCategoryRequiredToAddScan}
                  >
                    <AppRegistrationIcon color={isCategoryRequiredToAddScan ? 'disabled' : 'primary'} />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <>
                <IconButton onClick={handleSave} data-testid="save-button" disabled={isLoading}>
                  {isSaving ? <SmallLoader /> : <CheckIcon color="success" />}
                </IconButton>
                <IconButton onClick={exitEditMode} disabled={isLoading}>
                  <CloseIcon color="error" />
                </IconButton>
              </>
            )}
          </Box>
        )}
      </Box>

      {isEditMode && (
        <ArtifactUploaderInput
          artifactTypeId={ARTIFACT_ID.NOTE_ATTACHMENT}
          isVisible
          isNewDocumentStorage
          label=""
          source="paths"
          maxElements={1}
          onChangeCallback={handleArtifactsUpload}
          value={tempArtifactIds}
        />
      )}

      {!isEditMode && scanFileData && (
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <DownloadFileLinkField
            metadataSource=""
            pathSource=""
            metadataValue={scanFileData.metadata}
            pathValue={scanFileData.path}
            permission={DOWNLOAD_EMPLOYEE_DOCUMENTS}
          />
          {canEdit && (
            <IconButton onClick={handleRemoveScanDocument} disabled={isDeleting}>
              {isDeleting ? <SmallLoader /> : <CloseIcon sx={{ fontSize: '14px' }} color="error" />}
            </IconButton>
          )}
        </Box>
      )}

      {!isEditMode && !scanFileData && <Typography variant="body2">Scan not uploaded</Typography>}
    </Box>
  );
};

export default ScanSection;
